import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue, useSetAtom } from "jotai";
import { useFragment } from "react-relay";

import { setCourseIdAtom } from "@/features/BookingWizard/atoms/actions/setCourse/setCourseAtom";
import { courseIdSelectorAtom } from "@/features/BookingWizard/atoms/selectors/courseIdSelectorAtom/courseIdSelectorAtom";
import { deriveIsRequest } from "@/features/BookingWizard/utils/deriveIsRequest";
import { SelectableTile } from "@whoosh/design-system/src/components/controls";
import { Text, VStack } from "@whoosh/design-system/src/components/primitive";
import { getNounOverride } from "@whoosh/utilities/src/FormatNounOverride/getNounOverride";

import { LessonCourseControlNode$key } from "./__generated__/LessonCourseControlNode.graphql";

type Props = {
  nodeRef: LessonCourseControlNode$key | null;
};

export const LessonCourseControl = ({ nodeRef }: Props) => {
  const data = useFragment(LessonCourseControlFragmentString, nodeRef);

  const facility = data?.firstStartSlot?.edges?.[0]?.node?.agenda?.facility;

  // Use the matching course for the current facility type when determining private/public permissions
  const isPrivate = facility?.club?.supportsPrivate ?? false;

  const isRequest = deriveIsRequest(facility);

  const canSelectVenue =
    !isRequest &&
    (isPrivate
      ? facility?.privateLessonsPermissionSet?.patronSelectVenue ?? false
      : facility?.publicLessonsPermissionSet?.patronSelectVenue ?? false);

  const currentCourseId = useAtomValue(courseIdSelectorAtom);

  const setCourseId = useSetAtom(setCourseIdAtom);

  const courses = facility?.courses?.edges?.map((edge) => edge?.node);
  const eligibleCourseIds =
    data?.allLessonStartSlotIds?.edges?.reduce<string[]>((ids, edge) => {
      const courseId = edge?.node?.course?.id;

      return courseId ? [...ids, courseId] : ids;
    }, []) ?? [];

  const courseOptions = courses
    ?.map((course) => ({
      label: course?.name ?? "",
      description: course?.description ?? "",
      value: course?.id ?? "",
      isDisabled: !eligibleCourseIds.includes(course?.id ?? ""),
    }))
    ?.sort((a, b) => ((a.label || "") > (b.label || "") ? 1 : -1)); //sort label alphabetically for display
  const courseNoun = getNounOverride(
    facility?.copyOverrides?.course?.singular,
    t`Course`
  );

  return canSelectVenue ? (
    <VStack
      marginTop={2}
      backgroundColor="surface-primary"
      paddingHorizontal={5}
      paddingBottom={2}
    >
      <Text variant="mainBaseSemiBold" color="text-primary" marginVertical={4}>
        {t`Select ${courseNoun}`}
      </Text>
      <VStack gap={2}>
        {courseOptions?.map((course) => (
          <SelectableTile
            key={course.value}
            title={course.label ?? ""}
            subtitle={
              course.description
                ? course.description.length >= 40
                  ? course.description.substring(0, 40) + "..."
                  : course.description
                : ""
            }
            align="center"
            isDisabled={course.isDisabled}
            isSelected={currentCourseId === course.value}
            setSelected={() => {
              setCourseId({
                courseId: course.value ?? "",
                startSlotId:
                  data?.allLessonStartSlotIds?.edges?.find(
                    (edge) => edge?.node?.course?.id === course.value
                  )?.node?.id ?? null,
              });
            }}
          />
        ))}
      </VStack>
    </VStack>
  ) : null;
};

export const LessonCourseControlFragmentString = graphql`
  fragment LessonCourseControlNode on Node {
    ... on AvailabilitySlot {
      allLessonStartSlotIds: startSlots(
        slotAvailability: {
          facilityId: $facilityId
          bookingType: LESSON
          lessonTypeId: $lessonTypeId
        }
      ) {
        edges {
          node {
            id
            course {
              id
            }
          }
        }
      }
      firstStartSlot: startSlots(
        first: 1
        slotAvailability: {
          facilityId: $facilityId
          bookingType: LESSON
          lessonTypeId: $lessonTypeId
        }
      ) {
        edges {
          node {
            id
            agenda {
              facility {
                type
                club {
                  supportsPrivate
                }
                privateLessonsPermissionSet {
                  patronSelectVenue
                  patronAccess
                }
                publicLessonsPermissionSet {
                  patronSelectVenue
                  patronAccess
                }
                copyOverrides {
                  course {
                    singular
                  }
                }
                courses {
                  edges {
                    node {
                      courseBucket {
                        id
                      }
                      id
                      name
                      description
                      facility {
                        type
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
